import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getPreset, type PlayerInfo } from '@ic/engine';
import { Icon } from '../components/Icons';
import { Loader } from '../components/Loader';
import { api, auth, logout } from '../lib/auth';
import { idb, type SavedGame } from '../lib/idb';
import { send } from '../lib/socket';
import { activeRuleset, useStore } from '../lib/store';
import { syncNow } from '../lib/sync';

interface ProfileData {
  user: PlayerInfo & { ratingStandard: number; ratingCustom: number; ratingFour: number; createdAt: string };
  games: { id: string; white: string; black: string; result: string; reason: string; rulesetName: string; moves: number; endedAt: string; mode: string }[];
  stats: { played: number; won: number; lost: number; drawn: number };
}
interface Friend { name: string; status: 'accepted' | 'incoming' | 'outgoing'; online: boolean; rating: number }

export function Profile() {
  const { name } = useParams();
  const { user } = useStore(auth);
  const nav = useNavigate();
  const target = name ?? (user && !user.guest ? user.name : null);
  const isMe = !!user && !user.guest && target === user.name;
  const [data, setData] = useState<ProfileData | null>(null);
  const [err, setErr] = useState('');
  const [local, setLocal] = useState<SavedGame[]>([]);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [add, setAdd] = useState('');
  const [syncMsg, setSyncMsg] = useState('');

  const loadFriends = () => api<{ friends: Friend[] }>('/friends').then((r) => setFriends(r.friends)).catch(() => {});

  useEffect(() => {
    idb.all<SavedGame>('games').then((g) => setLocal(g.sort((a, b) => b.playedAt - a.playedAt)));
    if (!target) return;
    setErr('');
    api<ProfileData>(`/users/${encodeURIComponent(target)}`).then(setData).catch((e) => setErr((e as Error).message));
    if (isMe) loadFriends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, isMe]);

  const challenge = (friend: string) => {
    const rs = activeRuleset.get();
    send({ t: 'createInvite', tc: rs.rules.clock, rated: false, ruleset: rs ?? getPreset('standard'), color: 'random', to: friend });
    nav('/play/setup');
  };

  return (
    <div className="page profile">
      {!target && (
        <div className="card">
          <h1>Profile</h1>
          <p>Log in to get Elo ratings, an online game history and friends. Offline games are kept on this device and synced when you sign in.</p>
          <Link to="/login" className="btn primary">Log in or sign up</Link>
        </div>
      )}
      {target && (
        <div className="card profile-head">
          <div className="avatar big w">{target[0].toUpperCase()}</div>
          <div className="grow">
            <h1>{target}</h1>
            {err && <div className="error small">{err}</div>}
            {!data && !err && <Loader size="sm" label="Loading profile…" />}
            {data && (
              <div className="stat-row">
                <div className="stat"><b>{data.user.ratingStandard}</b><span>Standard</span></div>
                <div className="stat"><b>{data.user.ratingCustom}</b><span>Other modes</span></div>
                <div className="stat"><b>{data.user.ratingFour}</b><span>4 players</span></div>
                <div className="stat"><b>{data.stats.played}</b><span>Games</span></div>
                <div className="stat"><b>{data.stats.won}/{data.stats.drawn}/{data.stats.lost}</b><span>W/D/L</span></div>
              </div>
            )}
          </div>
          {isMe && (
            <div className="stack">
              <button className="btn secondary" onClick={async () => { const r = await syncNow(); setSyncMsg(r ? `Synced ${r.pushed} up, ${r.pulled} down` : 'Nothing to sync / offline'); }}>Sync now</button>
              <button className="btn ghost" onClick={() => { logout(); nav('/'); }}>Log out</button>
              {syncMsg && <span className="muted small">{syncMsg}</span>}
            </div>
          )}
        </div>
      )}

      <div className="profile-grid">
        {data && (
          <section className="card">
            <h2>Online games</h2>
            {data.games.length === 0 && <p className="muted">No games yet.</p>}
            <table className="games">
              <tbody>
                {data.games.map((g) => (
                  <tr key={g.id}>
                    <td>{g.white} – {g.black}</td>
                    <td><b>{g.result}</b></td>
                    <td className="muted small">{g.reason}</td>
                    <td className="muted small">{g.rulesetName}</td>
                    <td className="muted small">{new Date(g.endedAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
        {isMe && (
          <section className="card">
            <h2>Friends</h2>
            <form className="row gap" onSubmit={async (e) => {
              e.preventDefault();
              try { await api('/friends', { body: { username: add } }); setAdd(''); loadFriends(); } catch (x) { alert((x as Error).message); }
            }}>
              <input value={add} onChange={(e) => setAdd(e.target.value)} placeholder="Username" />
              <button className="btn secondary">Add</button>
            </form>
            <ul className="friend-list">
              {friends.map((f) => (
                <li key={f.name}>
                  <span className={`dot ${f.online ? 'on' : ''}`} />
                  <Link to={`/profile/${f.name}`}>{f.name}</Link> <span className="muted small">{f.rating}</span>
                  {f.status === 'incoming' && <button className="btn primary sm" onClick={() => api('/friends/accept', { body: { username: f.name } }).then(loadFriends)}>Accept</button>}
                  {f.status === 'outgoing' && <span className="muted small">pending</span>}
                  {f.status === 'accepted' && <button className="btn secondary sm" disabled={!f.online} onClick={() => challenge(f.name)}>Challenge</button>}
                </li>
              ))}
              {friends.length === 0 && <li className="muted small">No friends yet.</li>}
            </ul>
          </section>
        )}
        {(!name || isMe) && (
          <section className="card">
            <h2>Games on this device</h2>
            {local.length === 0 && <p className="muted">Local and bot games appear here.</p>}
            <table className="games">
              <tbody>
                {local.slice(0, 30).map((g) => (
                  <tr key={g.id}>
                    <td>{g.white} – {g.black}</td>
                    <td><b>{g.result}</b></td>
                    <td className="muted small">{g.reason}</td>
                    <td className="muted small">{g.rulesetName}</td>
                    <td className="muted small" title={g.synced ? 'Synced to your account' : 'Only on this device'}><Icon name={g.synced ? 'cloud' : 'device'} size={15} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </div>
  );
}
